import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdCategory,
  MdInventory,
  MdVisibility,
  MdClose,
  MdArrowBack,
  MdWarning,
  MdCheckCircle,
  MdCancel,
  MdDelete,
  MdEdit,
  MdDownload,
  MdScale
} from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const productTypes = [
  'S1',
  'S2',
  'S3',
  'SP1',
  'SP2',
  'SP3',
  'P1',
  'P2',
  'P3',
  'SSP1',
  'SSP2',
  'SSP3',
  'G1',
  'G2',
  'G3',
  'F1',
  'F2',
  'F3',
  'X1',
  'X2',
  'X3'
];

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const PiecesRecords = () => {
  const navigate = useNavigate();
  
  // State for API data
  const [piecesRecords, setPiecesRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    product: '',
    recommendation: ''
  });
  const [userRole, setUserRole] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [downloading, setDownloading] = useState(false);
  const itemsPerPage = 10;

  // Fetch data from API
  const fetchPiecesRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://usibras-web-backend.onrender.com/api/analyst-pieces', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data) {
        setPiecesRecords(result.data);
      } else {
        setPiecesRecords([]);
      }
    } catch (error) {
      console.error('Error fetching pieces records:', error);
      setError('Failed to load pieces records. Please try again later.');
      setPiecesRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Check user role and fetch data on component mount
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }

    fetchPiecesRecords();
  }, []);

  // Apply filters and search
  const filteredData = piecesRecords.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.batch_code,
      item.product,
      item.analyst,
      item.entry_date,
      item.recommendation,
      item.observation,
      item.quality_manager,
      item.rework
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    // Convert date strings to Date objects for comparison
    const itemDate = new Date(item.entry_date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    const matchesDate = (!startDate || itemDate >= startDate) &&
                       (!endDate || itemDate <= endDate);
    
    const matchesProduct = !filters.product || item.product === filters.product;
    
    const matchesRecommendation = !filters.recommendation || item.recommendation === filters.recommendation;

    return matchesSearch && matchesDate && matchesProduct && matchesRecommendation;
  });

  // Calculate summary statistics
  const totalRecords = filteredData.length;
  const approvedRecords = filteredData.filter(item => item.recommendation === 'approved').length;
  const rejectedRecords = filteredData.filter(item => item.recommendation === 'rejected').length;
  const totalWeight = filteredData.length > 0
    ? filteredData.reduce((sum, item) => sum + parseFloat(item.weight || 0), 0).toFixed(2)
    : '0.00';

  // Get unique values for filters
  const uniqueProducts = [...new Set(piecesRecords.map(item => item.product))].filter(Boolean);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["ID", "Batch Code", "Product", "Analyst", "Monoblocks", "Weight (kg)", "Status", "Rework", "Date", "Time", "Actions"];

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Format date for input field (YYYY-MM-DD format)
  const formatDateForInput = (dateString) => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  // Handle edit record
  const handleEditRecord = (record) => {
    setRecordToEdit(record);
    setEditFormData({
      batch_code: record.batch_code || '',
      product: record.product || '',
      analyst: record.analyst || '',
      supervisor: record.supervisor || '',
      quality_manager: record.quality_manager || '',
      entry_date: formatDateForInput(record.entry_date),
      moisture: record.moisture || '',
      weight: record.weight || '',
      num_monoblocks: record.num_monoblocks || '',
      recommendation: record.recommendation || 'approved',
      observation: record.observation || '',
      rework: record.rework || '',
      sieve_1_percentage: record.sieve_1_percentage || '',
      sieve_1_size: record.sieve_1_size || '',
      sieve_2_percentage: record.sieve_2_percentage || '',
      sieve_2_size: record.sieve_2_size || '',
      sieve_3_percentage: record.sieve_3_percentage || '',
      sieve_3_size: record.sieve_3_size || '',
      sieve_4_percentage: record.sieve_4_percentage || '',
      sieve_4_size: record.sieve_4_size || '',
      spotted_percentage: record.spotted_percentage || '',
      dessert_percentage: record.dessert_percentage || '',
      rotten_percentage: record.rotten_percentage || '',
      wholes_percentage: record.wholes_percentage || '',
      "1st_quality_per": record["1st_quality_per"] || record.first_quality_percentage || '',
      "2nd_quality_per": record["2nd_quality_per"] || record.second_quality_percentage || '',
      "3rd_quality_per": record["3rd_quality_per"] || record.third_quality_percentage || '',
      peels_percentage: record.peels_percentage || '',
      broken_percentage: record.broken_percentage || '',
      dirty_percentage: record.dirty_percentage || '',
      clean_percentage: record.clean_percentage || '',
      splits_percentage: record.splits_percentage || '',
      pieces_percentage: record.pieces_percentage || '',
      butts_percentage: record.butts_percentage || '',
      black_point_percentage: record.black_point_percentage || ''
    });
    setShowEditModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  // Handle delete confirmation
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('usibras_login');
      const response = await fetch(`https://usibras-web-backend.onrender.com/api/analyst-pieces/${recordToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove the deleted record from the state
      setPiecesRecords(prev => prev.filter(record => record.id !== recordToDelete.id));
      
      // Close the delete modal
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
    } catch (error) {
      console.error('Error deleting record:', error);
      setError('Failed to delete record. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setRecordToEdit(null);
    setEditFormData({});
  };

  // Handle edit form input changes
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    if (!recordToEdit) return;

    try {
      setEditing(true);
      const token = localStorage.getItem('usibras_login');
      
      // Prepare the request payload according to API specification
      const requestPayload = {
        product: editFormData.product || '',
        batch_code: editFormData.batch_code || '',
        supervisor: editFormData.supervisor || '',
        analyst: editFormData.analyst || '',
        num_monoblocks: parseInt(editFormData.num_monoblocks) || 0,
        moisture: parseFloat(editFormData.moisture) || 0,
        weight: parseFloat(editFormData.weight) || 0,
        sieve_1_percentage: parseFloat(editFormData.sieve_1_percentage) || 0,
        sieve_1_size: editFormData.sieve_1_size || '',
        sieve_2_percentage: parseFloat(editFormData.sieve_2_percentage) || 0,
        sieve_2_size: editFormData.sieve_2_size || '',
        sieve_3_percentage: parseFloat(editFormData.sieve_3_percentage) || 0,
        sieve_3_size: editFormData.sieve_3_size || '',
        sieve_4_percentage: parseFloat(editFormData.sieve_4_percentage) || 0,
        sieve_4_size: editFormData.sieve_4_size || '',
        spotted_percentage: parseFloat(editFormData.spotted_percentage) || 0,
        dessert_percentage: parseFloat(editFormData.dessert_percentage) || 0,
        rotten_percentage: parseFloat(editFormData.rotten_percentage) || 0,
        wholes_percentage: parseFloat(editFormData.wholes_percentage) || 0,
        "1st_quality_per": parseFloat(editFormData["1st_quality_per"]) || 0,
        "2nd_quality_per": parseFloat(editFormData["2nd_quality_per"]) || 0,
        "3rd_quality_per": parseFloat(editFormData["3rd_quality_per"]) || 0,
        peels_percentage: parseFloat(editFormData.peels_percentage) || 0,
        broken_percentage: parseFloat(editFormData.broken_percentage) || 0,
        dirty_percentage: parseFloat(editFormData.dirty_percentage) || 0,
        clean_percentage: parseFloat(editFormData.clean_percentage) || 0,
        splits_percentage: parseFloat(editFormData.splits_percentage) || 0,
        pieces_percentage: parseFloat(editFormData.pieces_percentage) || 0,
        butts_percentage: parseFloat(editFormData.butts_percentage) || 0,
        black_point_percentage: parseFloat(editFormData.black_point_percentage) || 0,
        recommendation: editFormData.recommendation || 'approved',
        observation: editFormData.observation || '',
        rework: editFormData.rework || '',
        entry_date: editFormData.entry_date || '',
        quality_manager: editFormData.quality_manager || ''
      };

      const response = await fetch(`https://usibras-web-backend.onrender.com/api/analyst-pieces/${recordToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message === 'Record updated successfully') {
        // Update the record in the state
        setPiecesRecords(prev => prev.map(record => 
          record.id === recordToEdit.id 
            ? { ...record, ...editFormData }
            : record
        ));
        
        // Close the edit modal
        closeEditModal();
      } else {
        throw new Error('Unexpected response format');
      }
      
    } catch (error) {
      console.error('Error updating record:', error);
      setError('Failed to update record. Please try again later.');
    } finally {
      setEditing(false);
    }
  };

  // Handle download data to Excel
  const handleDownloadData = async () => {
    try {
      setDownloading(true);
      
      // Check if there's filtered data to export
      if (filteredData.length === 0) {
        setError('No data to export. Please adjust your filters.');
        setDownloading(false);
        return;
      }
      
      // Dynamic import of XLSX library
      const XLSX = await import('xlsx');
      
      // Prepare data for Excel export (using filteredData instead of all records)
      const exportData = filteredData.map(record => ({
        'ID': record.id,
        'Batch Code': record.batch_code,
        'Product': record.product,
        'Supervisor': record.supervisor,
        'Analyst': record.analyst,
        'Quality Manager': record.quality_manager,
        'Entry Date': formatDate(record.entry_date),
        'Moisture (%)': record.moisture,
        'Weight (kg)': record.weight,
        'Number of Monoblocks': record.num_monoblocks,
        'Recommendation': record.recommendation,
        'Rework': record.rework,
        'Observation': record.observation,
        'Sieve 1 %': record.sieve_1_percentage,
        'Sieve 1 Size': record.sieve_1_size,
        'Sieve 2 %': record.sieve_2_percentage,
        'Sieve 2 Size': record.sieve_2_size,
        'Sieve 3 %': record.sieve_3_percentage,
        'Sieve 3 Size': record.sieve_3_size,
        'Sieve 4 %': record.sieve_4_percentage,
        'Sieve 4 Size': record.sieve_4_size,
        'Spotted %': record.spotted_percentage,
        'Dessert %': record.dessert_percentage,
        'Rotten %': record.rotten_percentage,
        'Wholes %': record.wholes_percentage,
        '1st Quality %': record["1st_quality_per"] || record.first_quality_percentage,
        '2nd Quality %': record["2nd_quality_per"] || record.second_quality_percentage,
        '3rd Quality %': record["3rd_quality_per"] || record.third_quality_percentage,
        'Peels %': record.peels_percentage,
        'Broken %': record.broken_percentage,
        'Dirty %': record.dirty_percentage,
        'Clean %': record.clean_percentage,
        'Splits %': record.splits_percentage,
        'Pieces %': record.pieces_percentage,
        'Butts %': record.butts_percentage,
        'Black Point %': record.black_point_percentage,
        'Created At': formatDateTime(record.created_at)
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const columnWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pieces Analysis Records');

      // Generate filename with current date and filter indication
      const currentDate = new Date().toISOString().split('T')[0];
      const hasFilters = filters.startDate || filters.endDate || filters.product || filters.recommendation || searchTerm;
      const filename = hasFilters 
        ? `pieces_analysis_records_filtered_${currentDate}.xlsx`
        : `pieces_analysis_records_${currentDate}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

    } catch (error) {
      console.error('Error downloading data:', error);
      setError('Failed to download data. Please try again later.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Card extra={"w-full h-full p-4"}>
        {/* Header with back button */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex w-fit items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                Pieces Analysis Records
              </h4>
              <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                Detailed view of all pieces analysis records and quality assessments
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadData}
              className="flex w-fit items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
              disabled={downloading || loading || filteredData.length === 0}
            >
              {downloading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <MdDownload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{downloading ? 'Downloading...' : 'Download Excel'}</span>
            </button>
            <button
              onClick={fetchPiecesRecords}
              className="flex w-fit items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              disabled={loading}
            >
              <MdSearch className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6">
          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-blue-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-blue-600 dark:text-blue-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Records</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-green-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Approved</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{approvedRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-red-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdCancel className="h-5 w-5 text-red-600 dark:text-red-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rejected</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{rejectedRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-orange-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdScale className="h-5 w-5 text-orange-600 dark:text-orange-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Weight</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalWeight} kg</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-3 dark:border-navy-700 dark:bg-navy-800 sm:p-4">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
            <h3 className="text-base font-semibold text-gray-700 dark:text-white sm:text-lg">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Product</label>
              <select
                value={filters.product}
                onChange={(e) => setFilters({...filters, product: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Products</option>
                {uniqueProducts.map((product) => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Status</label>
              <select
                value={filters.recommendation}
                onChange={(e) => setFilters({...filters, recommendation: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex justify-end sm:mt-4">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', product: '', recommendation: ''})}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:w-auto">
            <div className="flex h-8 items-center rounded-full bg-white px-3 dark:bg-navy-900 sm:h-10 sm:px-4">
              <span className="text-xl">
                <MdSearch className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 w-full bg-transparent text-xs font-medium text-gray-600 outline-none dark:text-white sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchPiecesRecords}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : piecesRecords.length === 0 ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-gray-400">
                <MdInventory className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                No records found
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                No pieces analysis records are available at the moment.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-2 pr-2 text-start dark:!border-navy-700 sm:pb-[10px] sm:pr-4"
                    >
                      <p className="text-xs font-bold tracking-wide text-gray-800 dark:text-white sm:text-sm">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.batch_code}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.product}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.analyst}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.num_monoblocks}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.weight || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium sm:px-2 sm:py-1 ${
                        item.recommendation === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {item.recommendation === 'approved' && <MdCheckCircle className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        {item.recommendation === 'rejected' && <MdCancel className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        <span className="hidden sm:inline">{item.recommendation}</span>
                        <span className="sm:hidden">{item.recommendation === 'approved' ? 'OK' : 'NO'}</span>
                      </span> 
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium sm:px-2 sm:py-1 ${
                        item.rework === 'Yes'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {item.rework === 'Yes' && <MdWarning className="mr-1 h-2.5 w-2.5 sm:h-3 sm:w-3" />}
                        <span className="hidden sm:inline">{item.rework || 'N/A'}</span>
                        <span className="sm:hidden">{item.rework === 'Yes' ? 'Y' : item.rework === 'No' ? 'N' : '-'}</span>
                      </span>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatDate(item.entry_date)}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatTime(item.created_at || item.entry_date)}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="rounded-lg bg-blue-100 p-1.5 text-blue-600 hover:bg-blue-200 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600 sm:p-2"
                          title="View Details"
                        >
                          <MdVisibility className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleEditRecord(item)}
                          className="rounded-lg bg-green-100 p-1.5 text-green-600 hover:bg-green-200 dark:bg-navy-700 dark:text-green-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Edit Record"
                        >
                          <MdEdit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="rounded-lg bg-red-100 p-1.5 text-red-600 hover:bg-red-200 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Delete Record"
                        >
                          <MdDelete className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredData.length > 0 && (
          <div className="mt-4 flex flex-col gap-4 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-medium text-navy-700 dark:text-white sm:text-sm">
              Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
            </p>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">1</span>
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>
              
              {/* Page number buttons */}
              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = index + 1;
                } else if (currentPage <= 2) {
                  pageNum = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNum = totalPages - 2 + index;
                } else {
                  pageNum = currentPage - 1 + index;
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                      currentPage === pageNum
                        ? "bg-brand-700 text-white"
                        : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">{totalPages}</span>
              </button>
            </div>
          </div>
                 )}

        {/* Details Modal */}
        {showModal && selectedRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Pieces Analysis Details
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    Batch Code: {selectedRecord.batch_code}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                {/* Basic Information */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Basic Information</h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Batch Code:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.batch_code}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Product:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.product}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Supervisor:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.supervisor}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Analyst:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.analyst}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Quality Manager:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.quality_manager}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Entry Date:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{formatDate(selectedRecord.entry_date)}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Moisture:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.moisture}%</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Weight:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.weight} kg</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Number of Monoblocks:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.num_monoblocks}</span>
                    </div>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Quality Assessment</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Recommendation:</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        selectedRecord.recommendation === 'approved'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {selectedRecord.recommendation === 'approved' && <MdCheckCircle className="mr-1 h-3 w-3" />}
                        {selectedRecord.recommendation === 'rejected' && <MdCancel className="mr-1 h-3 w-3" />}
                        {selectedRecord.recommendation}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rework:</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        selectedRecord.rework === 'Yes'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {selectedRecord.rework === 'Yes' && <MdWarning className="mr-1 h-3 w-3" />}
                        {selectedRecord.rework || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sieve Analysis */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Sieve Analysis</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Sieve 1</p>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">{selectedRecord.sieve_1_percentage}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedRecord.sieve_1_size}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Sieve 2</p>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">{selectedRecord.sieve_2_percentage}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedRecord.sieve_2_size}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Sieve 3</p>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">{selectedRecord.sieve_3_percentage}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedRecord.sieve_3_size}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Sieve 4</p>
                      <p className="text-sm font-bold text-navy-700 dark:text-white">{selectedRecord.sieve_4_percentage}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedRecord.sieve_4_size}</p>
                    </div>
                  </div>
                </div>

                {/* Quality Percentages */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Quality Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Spotted</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{selectedRecord.spotted_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Dessert</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{selectedRecord.dessert_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Rotten</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{selectedRecord.rotten_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Wholes</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{selectedRecord.wholes_percentage}%</p>
                    </div>
                  </div>
                </div>

                {/* Defect Percentages */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Defect Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Peels</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.peels_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Broken</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.broken_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Dirty</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.dirty_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Clean</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.clean_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Splits</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.splits_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Pieces</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.pieces_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Butts</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.butts_percentage}%</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                      <p className="text-xs font-medium text-red-600 dark:text-red-400">Black Point</p>
                      <p className="text-lg font-bold text-red-700 dark:text-red-300">{selectedRecord.black_point_percentage}%</p>
                    </div>
                  </div>
                </div>

                {/* Observation */}
                {selectedRecord.observation && (
                  <div className="space-y-4 lg:col-span-2">
                    <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Observation</h4>
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <p className="text-sm text-navy-700 dark:text-white">{selectedRecord.observation}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Timestamps</h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Created At:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{formatDateTime(selectedRecord.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-4 flex justify-end border-t border-gray-200 pt-3 dark:border-navy-600 sm:mt-6 sm:pt-4">
                <button
                  onClick={closeModal}
                  className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && recordToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-md rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Delete Record
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    This action cannot be undone
                  </p>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/20">
                    <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-white">
                      Are you sure you want to delete this record?
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {recordToDelete.id}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                  <p className="text-xs text-red-700 dark:text-red-300">
                    This will permanently delete the pieces analysis record and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && recordToEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              {/* Modal Header */}
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Edit Pieces Analysis Record
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    ID: {recordToEdit.id} | Batch Code: {recordToEdit.batch_code}
                  </p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Edit Form */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Basic Information</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Batch Code</label>
                      <input
                        type="text"
                        value={editFormData.batch_code || ''}
                        onChange={(e) => handleEditFormChange('batch_code', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Product</label>
                      <select
                        value={editFormData.product || ''}
                        onChange={(e) => handleEditFormChange('product', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Product</option>
                        {productTypes.map((product) => (
                          <option key={product} value={product}>{product}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Analyst</label>
                      <input
                        type="text"
                        value={editFormData.analyst || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Supervisor</label>
                      <input
                        type="text"
                        value={editFormData.supervisor || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Quality Manager</label>
                      <input
                        type="text"
                        value={editFormData.quality_manager || ''}
                        readOnly
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Entry Date</label>
                      <input
                        type="date"
                        value={editFormData.entry_date || ''}
                        onChange={(e) => handleEditFormChange('entry_date', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Moisture %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.moisture || ''}
                        onChange={(e) => handleEditFormChange('moisture', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.weight || ''}
                        onChange={(e) => handleEditFormChange('weight', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Number of Monoblocks</label>
                      <select
                        value={editFormData.num_monoblocks || ''}
                        onChange={(e) => handleEditFormChange('num_monoblocks', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Number</option>
                        {Array.from({ length: 30 }, (_, i) => i + 1).map((number) => (
                          <option key={number} value={number}>{number}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Recommendation</label>
                      <select
                        value={editFormData.recommendation || 'approved'}
                        onChange={(e) => handleEditFormChange('recommendation', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rework</label>
                      <select
                        value={editFormData.rework || ''}
                        onChange={(e) => handleEditFormChange('rework', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      >
                        <option value="">Select Rework Status</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Sieve Analysis */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Sieve Analysis</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 1 %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.sieve_1_percentage || ''}
                        onChange={(e) => handleEditFormChange('sieve_1_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 1 Size</label>
                      <input
                        type="text"
                        value={editFormData.sieve_1_size || ''}
                        onChange={(e) => handleEditFormChange('sieve_1_size', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 2 %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.sieve_2_percentage || ''}
                        onChange={(e) => handleEditFormChange('sieve_2_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 2 Size</label>
                      <input
                        type="text"
                        value={editFormData.sieve_2_size || ''}
                        onChange={(e) => handleEditFormChange('sieve_2_size', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 3 %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.sieve_3_percentage || ''}
                        onChange={(e) => handleEditFormChange('sieve_3_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 3 Size</label>
                      <input
                        type="text"
                        value={editFormData.sieve_3_size || ''}
                        onChange={(e) => handleEditFormChange('sieve_3_size', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 4 %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.sieve_4_percentage || ''}
                        onChange={(e) => handleEditFormChange('sieve_4_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Sieve 4 Size</label>
                      <input
                        type="text"
                        value={editFormData.sieve_4_size || ''}
                        onChange={(e) => handleEditFormChange('sieve_4_size', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Quality Percentages */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Quality Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Spotted %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.spotted_percentage || ''}
                        onChange={(e) => handleEditFormChange('spotted_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Dessert %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.dessert_percentage || ''}
                        onChange={(e) => handleEditFormChange('dessert_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Rotten %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.rotten_percentage || ''}
                        onChange={(e) => handleEditFormChange('rotten_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Wholes %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.wholes_percentage || ''}
                        onChange={(e) => handleEditFormChange('wholes_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">1st Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData["1st_quality_per"] || ''}
                        onChange={(e) => handleEditFormChange('1st_quality_per', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">2nd Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData["2nd_quality_per"] || ''}
                        onChange={(e) => handleEditFormChange('2nd_quality_per', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">3rd Quality %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData["3rd_quality_per"] || ''}
                        onChange={(e) => handleEditFormChange('3rd_quality_per', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Defect Percentages */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Defect Percentages</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Peels %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.peels_percentage || ''}
                        onChange={(e) => handleEditFormChange('peels_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Broken %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.broken_percentage || ''}
                        onChange={(e) => handleEditFormChange('broken_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Dirty %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.dirty_percentage || ''}
                        onChange={(e) => handleEditFormChange('dirty_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Clean %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.clean_percentage || ''}
                        onChange={(e) => handleEditFormChange('clean_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Splits %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.splits_percentage || ''}
                        onChange={(e) => handleEditFormChange('splits_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Pieces %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.pieces_percentage || ''}
                        onChange={(e) => handleEditFormChange('pieces_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Butts %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.butts_percentage || ''}
                        onChange={(e) => handleEditFormChange('butts_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Black Point %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editFormData.black_point_percentage || ''}
                        onChange={(e) => handleEditFormChange('black_point_percentage', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Observation */}
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                  <h4 className="mb-4 text-base font-semibold text-navy-700 dark:text-white sm:text-lg">Observation</h4>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Notes</label>
                    <textarea
                      value={editFormData.observation || ''}
                      onChange={(e) => handleEditFormChange('observation', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
                <button
                  onClick={closeEditModal}
                  disabled={editing}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={editing}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {editing ? 'Updating...' : 'Update Record'}
                </button>
              </div>
            </div>
          </div>
        )}
       </Card>
     </div>
   );
 };

export default PiecesRecords;